/** Mock producer screen — pixel-perfect match of the real Producer page at 1920x1080. */

// ── Exact CSS variable values ──
const C = {
  bg: "#0a0a0b", surface: "#111113", surface2: "#1a1a1e", surface3: "#222228",
  border: "#2a2a32", text: "#e4e4e7", textMuted: "#71717a", textDim: "#52525b",
  accent: "#3b82f6", danger: "#ef4444", success: "#22c55e", warning: "#eab308",
};
const F = { sans: "'Inter', sans-serif", mono: "'JetBrains Mono', monospace" };

export interface ProducerRider {
  name: string;
  nickname: string;
  num: string;
  focused: boolean;
  attempts: (number | null)[];
  current: number;
  colour: string;
  /** Per-judge, per-attempt scores: judges[judgeName][attemptIndex] */
  judgeScores?: Record<string, (number | null)[]>;
}

export interface ProducerScreenProps {
  riders?: ProducerRider[];
  maxAttempts?: number;
  pulseAdvanceIndex?: number;
  scoringModel?: string;
}

const DEFAULT_RIDERS: ProducerRider[] = [
  { name: "Harley Clifford", nickname: "The King", num: "1", focused: true, attempts: [8.5, 7.9, null], current: 2, colour: C.accent,
    judgeScores: { Sarah: [8.0, 7.5, null], Mike: [9.0, 8.5, null], Lily: [8.5, 7.7, null] } },
  { name: "Nic Rapa", nickname: "Raptor", num: "7", focused: false, attempts: [7.2, 6.8, null], current: 2, colour: C.success,
    judgeScores: { Sarah: [7.0, 6.5, null], Mike: [7.5, 7.0, null], Lily: [7.1, 6.8, null] } },
  { name: "Dallas Friday", nickname: "D-Fri", num: "12", focused: false, attempts: [9.0, 8.8, null], current: 2, colour: C.warning,
    judgeScores: { Sarah: [9.0, 8.5, null], Mike: [9.0, 9.0, null], Lily: [9.0, 9.0, null] } },
  { name: "Amber Wing", nickname: "Wings", num: "3", focused: false, attempts: [6.5, 7.1, null], current: 2, colour: C.danger,
    judgeScores: { Sarah: [6.5, 7.0, null], Mike: [6.5, 7.5, null], Lily: [6.5, 6.8, null] } },
  { name: "Steel Lafferty", nickname: "Steele", num: "22", focused: false, attempts: [8.1, null, null], current: 1, colour: "#a855f7",
    judgeScores: { Sarah: [8.0, null, null], Mike: [8.5, null, null], Lily: [7.8, null, null] } },
  { name: "Meagan Ethell", nickname: "Mega", num: "5", focused: false, attempts: [8.0, 8.5, null], current: 2, colour: "#f97316",
    judgeScores: { Sarah: [8.0, 8.5, null], Mike: [8.0, 8.5, null], Lily: [8.0, 8.5, null] } },
];

export function ProducerScreen({ riders: ridersProp, maxAttempts = 3, pulseAdvanceIndex, scoringModel = "max" }: ProducerScreenProps = {}) {
  const riderData = ridersProp ?? DEFAULT_RIDERS;

  return (
    <div style={shell}>
      <div style={topbar}>
        <span style={brand}>RALEY</span>
        <nav style={{ display: "flex", gap: 16 }}>
          <span style={{ ...navLink, color: C.text }}>Competitions</span>
          <span style={navLink}>Riders</span>
          <span style={navLink}>Training</span>
        </nav>
        <span style={{ marginLeft: "auto", fontFamily: F.mono, fontSize: 11, color: C.textMuted }}>producer@koda-software.com</span>
      </div>

      <div style={{ padding: 24 }}>
        {/* Page header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, paddingBottom: 16, borderBottom: `1px solid ${C.border}` }}>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 800, textTransform: "uppercase" as const, letterSpacing: 1, margin: 0 }}>Producer</h1>
            <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 2 }}>
              <span style={{ fontSize: 13, color: C.textMuted }}>Boardstock Invitational</span>
            </div>
            <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 4 }}>
              <span style={{ ...badge, background: C.danger, color: "#fff" }}>LIVE</span>
              <select style={{ minWidth: 180, fontFamily: F.sans, fontSize: 14, padding: "6px 12px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text }}>
                <option>Finals — Men's Pro</option>
              </select>
              <span style={btnSecondary}>Edit Heats</span>
              <span style={btnDanger}>Go Dark</span>
            </div>
          </div>
        </div>

        {/* Overlay controls */}
        <div style={{ display: "flex", gap: 12, marginBottom: 24 }}>
          <span style={btnPrimary}>Hide Heat Name</span>
          <span style={btnSecondary}>Show Leaderboard</span>
          <span style={btnPrimary}>Hide Heat Riders</span>
          <span style={btnSecondary}>Show OBS Scores</span>
          <span style={btnSecondary}>Clear Focus</span>
        </div>

        {/* Overlay style switcher */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontFamily: F.mono, fontSize: 11, fontWeight: 500, textTransform: "uppercase" as const, letterSpacing: 0.5, color: C.textMuted, marginBottom: 8 }}>
            Overlay Style
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {["Steel", "Olympic", "Broadcast", "Neon", "Clean"].map((s, i) => (
              <span key={s} style={i === 0 ? btnSmPrimary : btnSmSecondary}>{s}</span>
            ))}
          </div>
        </div>

        {/* Rider control cards — matches .control-grid */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
          {riderData.map((r, idx) => {
            const validAttempts = r.attempts.filter((a): a is number => a !== null);
            const bestAvg = validAttempts.length > 0 ? Math.max(...validAttempts) : null;
            const isPulse = pulseAdvanceIndex === idx;
            const judges = r.judgeScores ?? {};
            const judgeNames = Object.keys(judges);

            return (
              <div key={r.name} style={{
                background: C.surface, border: `1px solid ${r.focused ? C.accent : C.border}`,
                padding: 12, display: "flex", flexDirection: "column" as const, gap: 8,
              }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{r.name}</div>
                <div style={{ fontSize: 11, color: C.textMuted, fontFamily: F.mono }}>
                  {r.nickname}
                </div>

                {/* Per-attempt score boxes + final */}
                <div style={{ display: "flex", gap: 4, marginTop: 6, alignItems: "center", flexWrap: "wrap" as const }}>
                  {r.attempts.map((avg, i) => {
                    const isBest = avg !== null && bestAvg !== null && avg === bestAvg;
                    return (
                      <div key={i} style={{
                        background: isBest ? C.accent : C.surface2,
                        border: `1px solid ${isBest ? C.accent : C.border}`,
                        borderRadius: 3, padding: "1px 5px", fontSize: 10, fontFamily: F.mono,
                        color: isBest ? "#fff" : avg != null ? C.text : C.textMuted,
                        fontWeight: isBest ? 700 : 400, minWidth: 24, textAlign: "center" as const,
                      }}>
                        {avg != null ? avg.toFixed(1) : "-"}
                      </div>
                    );
                  })}
                  <div style={{ fontSize: 11, fontWeight: 700, fontFamily: F.mono, color: bestAvg != null ? C.accent : C.textMuted, marginLeft: 2 }}>
                    {bestAvg != null ? bestAvg.toFixed(1) : "-"}
                  </div>
                </div>

                {/* Attempt dots + controls */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, marginBottom: 4 }}>
                  <span style={{ ...btnGhost, padding: "2px 6px", fontSize: 11 }}>-</span>
                  <div style={{ display: "flex", gap: 3, alignItems: "center" }}>
                    {Array.from({ length: maxAttempts }, (_, i) => {
                      const attemptNum = i + 1;
                      const isActive = attemptNum <= r.current;
                      const isCurrent = attemptNum === r.current;
                      return (
                        <div key={i} style={{
                          width: 8, height: 8, borderRadius: "50%",
                          background: isActive ? C.accent : "transparent",
                          border: `1px solid ${isActive ? C.accent : C.border}`,
                          boxShadow: isCurrent ? `0 0 0 2px ${C.accent}` : "none",
                          opacity: isActive ? 1 : 0.3,
                        }} />
                      );
                    })}
                  </div>
                  <span style={{
                    ...btnGhost, padding: "2px 6px", fontSize: 11,
                    ...(isPulse ? { color: C.success, border: `1px solid ${C.success}`, boxShadow: `0 0 8px ${C.success}` } : {}),
                  }}>+</span>
                  <span style={{ fontFamily: F.mono, fontSize: 10, marginLeft: 4, color: C.textMuted }}>{r.current}/{maxAttempts}</span>
                </div>

                {/* Actions: Focus + Show Scores */}
                <div style={{ display: "flex", gap: 8, marginTop: "auto" }}>
                  <span style={r.focused ? btnSmPrimary : { ...btnGhost, fontSize: 11, padding: "4px 12px" }}>
                    {r.focused ? "Unfocus" : "Focus"}
                  </span>
                  <span style={{ ...btnGhost, fontSize: 11, padding: "4px 12px" }}>OBS</span>
                  <span style={{ ...btnGhost, fontSize: 11, padding: "4px 12px" }}>Scores</span>
                </div>

                {/* Judge scores table — always visible, matches Producer.tsx lines 604-661 */}
                {judgeNames.length > 0 && (
                  <div style={{ marginTop: 8, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" as const, fontSize: 10, fontFamily: F.mono }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: "left" as const, padding: "2px 4px", color: C.textMuted, fontWeight: 600 }}></th>
                          {Array.from({ length: maxAttempts }, (_, a) => (
                            <th key={a} style={{ textAlign: "center" as const, padding: "2px 4px", color: C.textMuted, fontWeight: 600 }}>A{a + 1}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {judgeNames.map((name) => (
                          <tr key={name}>
                            <td style={{ padding: "2px 4px", color: C.textMuted, fontWeight: 600 }}>{name}</td>
                            {(judges[name] ?? []).slice(0, maxAttempts).map((score, a) => (
                              <td key={a} style={{ textAlign: "center" as const, padding: "2px 4px", color: score != null ? C.text : C.textMuted }}>
                                {score != null ? score.toFixed(1) : "-"}
                              </td>
                            ))}
                          </tr>
                        ))}
                        <tr style={{ borderTop: `1px solid ${C.border}` }}>
                          <td style={{ padding: "3px 4px", fontWeight: 700, color: C.textMuted }}>Final</td>
                          {r.attempts.map((avg, a) => (
                            <td key={a} style={{ textAlign: "center" as const, padding: "3px 4px", fontWeight: 700, color: avg != null ? C.accent : C.textMuted }}>
                              {avg != null ? avg.toFixed(1) : "-"}
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                    {bestAvg != null && (
                      <div style={{ marginTop: 4, fontSize: 11, fontWeight: 700, color: C.accent, fontFamily: F.mono }}>
                        Overall: {bestAvg.toFixed(1)} ({scoringModel})
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const shell: React.CSSProperties = { width: "100%", height: "100%", background: C.bg, fontFamily: F.sans, color: C.text, display: "flex", flexDirection: "column" };
const topbar: React.CSSProperties = { height: 48, background: C.surface, borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 24, padding: "0 24px", flexShrink: 0 };
const brand: React.CSSProperties = { fontFamily: F.mono, fontWeight: 800, fontSize: 15, letterSpacing: 2 };
const navLink: React.CSSProperties = { fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: C.textMuted };
const badge: React.CSSProperties = { display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, fontFamily: F.mono };
const btnPrimary: React.CSSProperties = { display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "4px 12px", background: C.accent, color: "#fff", border: `1px solid ${C.accent}`, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, fontFamily: F.sans };
const btnSecondary: React.CSSProperties = { display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "4px 12px", background: C.surface2, color: C.text, border: `1px solid ${C.border}`, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, fontFamily: F.sans };
const btnDanger: React.CSSProperties = { display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "4px 12px", background: "transparent", color: C.danger, border: `1px solid ${C.danger}`, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, fontFamily: F.sans };
const btnGhost: React.CSSProperties = { display: "inline-flex", alignItems: "center", justifyContent: "center", background: "transparent", color: C.textMuted, border: "1px solid transparent", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, fontFamily: F.sans };
const btnSmPrimary: React.CSSProperties = { ...btnPrimary, fontSize: 11, padding: "4px 12px" };
const btnSmSecondary: React.CSSProperties = { ...btnSecondary, fontSize: 11, padding: "4px 12px" };
